import axios from "axios";
import { isExamPaperPath } from "../utils/examPaperPath";
import { examDeviceId, isExamCall, touchPoints } from "../utils/examDevice";

// Use only runtime-injected config (window.RUNTIME_CONFIG) — no env vars
const runtimeConfig = typeof window !== "undefined" ? window.RUNTIME_CONFIG : undefined;

const API_URL = runtimeConfig?.BACKEND_URL ? `${runtimeConfig.BACKEND_URL}/api` : undefined;
const DEBUG_AUTH = runtimeConfig?.DEBUG_AUTH === true || runtimeConfig?.DEBUG_AUTH === "true";

// Log runtime config for visibility
if (typeof window !== "undefined") {
  console.group("🔧 Runtime Config");
  console.log("window.RUNTIME_CONFIG =", runtimeConfig ?? "❌ not present");
  console.log("➡ API_URL =", API_URL);
  console.log("➡ DEBUG_AUTH =", DEBUG_AUTH);
  console.groupEnd();
}

// Fail fast if config is missing
if (!API_URL) {
  throw new Error("❌ API_URL is undefined. Check window.RUNTIME_CONFIG.BACKEND_URL in public/config.js or container entrypoint.");
}


const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 3.1 — which browser this is, for an exam that binds an attempt to the
    // device that started it (utils/examDevice.js). Exam calls only.
    if (isExamCall(config.url)) {
      config.headers["X-Exam-Device"] = examDeviceId();
      // A touch screen on a "Mac" is an iPad (iPadOS 13+): the no-phones rule
      // reads it (backend services/exam/accessRules.js).
      config.headers["X-Exam-Touch-Points"] = String(touchPoints());
    }
    // When sending FormData, delete Content-Type so Axios auto-sets it
    // with the correct multipart/form-data boundary string
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
// Guard: prevent multiple concurrent 401s from all triggering redirect
let isRedirecting401 = false;

// ─── Hydration guard ───
// On page refresh, dashboard components fire API calls immediately.
// With Azure Container Apps (min_replicas=0), cold start can take 30+ seconds.
// During this time, some API calls may fail with 401/timeout before the backend
// is ready. Without this guard, the interceptor would nuke localStorage and
// redirect to /login — even though the user has a valid session.
//
// Strategy:
// - For the first 30 seconds after page load, the interceptor does NOT redirect
//   on 401. It just rejects the promise so the component can handle it.
// - AuthContext's profile refresh determines if the token is genuinely expired.
//   If profile returns 401, AuthContext calls logout() → user becomes null →
//   PrivateRoute redirects to /login via React navigation.
// - After 30 seconds, any 401 triggers a redirect as usual (mid-session expiry).
const PAGE_LOAD_TIME = Date.now();
const HYDRATION_WINDOW_MS = 30000; // 30 seconds — covers Azure cold start

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // The account still has its temporary password (4.2): the server refuses
    // everything until it is changed. Mark the session so every page shows
    // "choose a new password" instead of a screen of refusals.
    if (error.response?.status === 403 && error.response?.data?.code === "PASSWORD_CHANGE_REQUIRED") {
      try {
        const saved = JSON.parse(localStorage.getItem("user") || "null");
        if (saved && !saved.mustChangePassword) {
          localStorage.setItem("user", JSON.stringify({ ...saved, mustChangePassword: true }));
        }
      } catch { /* the event below still tells the app */ }
      // Not over a paper being written (EXAM-DAY SAFETY): the exam screen stays;
      // the flag is kept and the change screen comes on the next page.
      if (typeof window !== "undefined" && !isExamPaperPath(window.location.pathname)) {
        window.dispatchEvent(new Event("auth:password-change-required"));
      }
    }
    if (error.response?.status === 401) {
      // Skip redirect when the caller explicitly opts out (e.g. AuthContext
      // profile refresh on page load — it handles 401 gracefully on its own)
      if (error.config?._skipAuthRedirect) {
        return Promise.reject(error);
      }

      // During hydration window, don't redirect — let AuthContext handle it.
      // AuthContext's profile refresh will determine if the token is truly expired.
      if (Date.now() - PAGE_LOAD_TIME < HYDRATION_WINDOW_MS) {
        if (DEBUG_AUTH) console.log("[AUTH] 401 suppressed during hydration window");
        return Promise.reject(error);
      }

      const requestUrl = String(error.config?.url || "");
      // A wrong second-step code (/auth/mfa/*) is an answer to show, not a
      // session that ended: there is no session yet.
      const isAuthRequest = /\/auth\/(login|register|mfa)\b/.test(requestUrl);
      const isOnLoginPage =
        typeof window !== "undefined" && window.location.pathname === "/login";

      if (!isAuthRequest && !isOnLoginPage && !isRedirecting401) {
        isRedirecting401 = true;
        // Save the current path so Login can redirect back after re-auth
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath && currentPath !== "/" && currentPath !== "/login") {
          localStorage.setItem("redirectAfterLogin", currentPath);
        }
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth service
export const authService = {
  // Which institution this sign-in page is for (its address) — public.
  institution: () => api.get("/auth/institution"),
  // institutionCode: only when the server said the identifier is held in more
  // than one institution (409 AMBIGUOUS_IDENTIFIER).
  login: async (identifier, password, institutionCode) => {
    try {
      if (DEBUG_AUTH) console.log("[AUTH] Login attempt with identifier:", identifier);
      const response = await api.post("/auth/lms/login", {
        identifier,
        password,
        ...(institutionCode ? { institutionCode } : {}),
      });
      if (DEBUG_AUTH) console.log("[AUTH] Login success:", response.data);
      return response;
    } catch (error) {
      if (DEBUG_AUTH) console.error("[AUTH] Login error:", error.response?.data || error.message);
      throw error;
    }
  },

  // Sign-in from the LMS (backend controllers/oidcController.js). The start is
  // a full-page navigation (the LMS's own sign-in, then back to our callback),
  // never an XHR; the one-time code the callback hands /launch is traded here
  // for the session — the session itself never rides in a URL.
  oidcStartUrl: (to) => `${API_URL}/auth/oidc/start?${new URLSearchParams({ app: "candidate", to: to || "" })}`,
  oidcExchange: (code) => api.post("/auth/oidc/exchange", { code, app: "candidate" }),

  // The second step of a teacher-with-exam-personas sign-in (W3.1; backend
  // controllers/mfaController.js). The pending token rides in the body — never
  // as the Authorization header, which it could not open anyway.
  mfaEnrol: (mfaToken) => api.post("/auth/mfa/enrol", { mfaToken }),
  mfaConfirm: (mfaToken, code) => api.post("/auth/mfa/enrol/confirm", { mfaToken, code }),
  mfaVerify: (mfaToken, { code, recoveryCode }) => api.post("/auth/mfa/verify", { mfaToken, code, recoveryCode }),

  register: async (userData) => {
    try {
      const response = await api.post("/auth/register", userData);
      return response;
    } catch (error) {
      throw error;
    }
  },

  forgotPassword: async (email) => {
    try {
      const response = await api.post("/auth/forgot-password", { email });
      return response;
    } catch (error) {
      throw error;
    }
  },

  resetPassword: async ({ email, otp, newPassword }) => {
    try {
      const response = await api.post("/auth/reset-password", {
        email,
        otp,
        newPassword,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
};

export default api;
